import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { buildArtistSlug } from '@/lib/artistSlug';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const themeClass = {
  'classical-gold': 'from-[#fff8e6] via-white to-[#f4ead0]',
  'modern-dark': 'from-[#0B1020] via-[#151A2F] to-[#2B1744]',
  'folk-warm': 'from-[#fff4e8] via-white to-[#e8fff5]',
  'minimal-light': 'from-white via-[#f8fafc] to-[#eef6ff]',
  'bold-stage': 'from-[#14002e] via-[#4A148C] to-[#FF6F00]',
};
const TRUST_BADGE_LABELS = {
  verified_profile: 'Verified Profile',
  verified_artist: 'Verified Artist',
  performance_verified: 'Performance Verified',
};

const getPublicTrustBadge = (artist = {}) => {
  const override = String(artist.trust_badge_override || '').trim();
  if (override === 'none') return null;
  if (TRUST_BADGE_LABELS[override]) return { tier: 1, label: TRUST_BADGE_LABELS[override], is_verified: true };
  if (artist.badge_label && artist.is_verified) return { tier: artist.badge_tier || 1, label: artist.badge_label, is_verified: true };
  if (artist.is_verified) return { tier: artist.badge_tier || 1, label: 'Verified Profile', is_verified: true };
  return { tier: 0, label: 'Unverified', is_verified: false };
};

const buildLocalEpk = (artist = {}) => {
  const slug = artist.slug || buildArtistSlug(artist) || artist.id;
  const imageUrls = [...(artist.portfolio_image_urls || []), artist.image_url].filter(Boolean).slice(0, 6);
  const videoUrls = (artist.portfolio_video_urls || []).filter(Boolean).slice(0, 4);
  const artForm = artist.subcategory || artist.category || 'Artist';
  const city = artist.city || artist.state || 'India';
  return {
    artist,
    settings: { auto_publish: true },
    version: { status: 'local-preview' },
    blueprint: {
      blueprint_version: '1.0',
      artist_id: artist.id,
      slug,
      theme: 'minimal-light',
      trust_badge: getPublicTrustBadge(artist),
      sections: {
        hero: {
          artist_name: artist.name || 'KalaManch Artist',
          art_form: artForm,
          city,
          tagline: `${artForm} from ${city}`,
          image_url: artist.image_url,
          profile_url: `/artist/${slug}`,
        },
        highlights: { title: 'Portfolio highlights', image_urls: imageUrls, video_urls: videoUrls },
        about: {
          title: 'About the artist',
          body: artist.bio || artist.native_bio || `${artist.name || 'This artist'} is building a KalaManch public portfolio.`,
          native_body: artist.native_bio,
        },
        services: { title: 'Available for', items: [artForm, 'Collaborations', 'Events'].filter(Boolean) },
        stats: {
          title: 'Profile signals',
          items: [
            { label: 'Portfolio photos', value: imageUrls.length },
            { label: 'Performance videos', value: videoUrls.length },
            { label: 'Trust score', value: artist.trust_score || 0 },
          ],
        },
        cta: { title: 'Book or collaborate through KalaManch', button_label: 'View public profile', url: `/artist/${slug}` },
      },
      updated_at: new Date().toISOString(),
    },
  };
};

const ArtistEpk = () => {
  const { slug } = useParams();
  const [epk, setEpk] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setStatus('loading');
      try {
        const response = await axios.get(`${API}/v1/epk/${slug}`, { withCredentials: true });
        if (mounted) {
          setEpk(response.data);
          setStatus('ready');
        }
      } catch (error) {
        try {
          const meResponse = await axios.get(`${API}/artist-auth/me`, { withCredentials: true });
          const artist = meResponse.data || {};
          const ownSlug = artist.slug || buildArtistSlug(artist) || artist.id;
          const slugTokens = new Set(String(slug || '').split('-').filter(Boolean));
          const ownTokens = String(ownSlug || '').split('-').filter(Boolean);
          const overlap = ownTokens.filter((token) => slugTokens.has(token)).length;
          if (mounted && (slug === ownSlug || slug === artist.id || overlap >= 2)) {
            setEpk(buildLocalEpk(artist));
            setStatus('ready');
            return;
          }
        } catch (fallbackError) {
          // Keep the public error state if the viewer is not the artist owner.
        }
        if (mounted) setStatus('error');
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const blueprint = epk?.blueprint || {};
  const sections = blueprint.sections || {};
  const hero = sections.hero || {};
  const highlights = sections.highlights || {};
  const about = sections.about || {};
  const stats = sections.stats || {};
  const services = sections.services || {};
  const cta = sections.cta || {};
  const isDark = ['modern-dark', 'bold-stage'].includes(blueprint.theme);

  return (
    <main className={`min-h-screen bg-gradient-to-br pt-24 sm:pt-28 ${themeClass[blueprint.theme] || themeClass['minimal-light']}`}>
      <SEO
        title={`${hero.artist_name || 'Artist'} Living EPK | KalaManch.art`}
        description={`${hero.artist_name || 'Artist'} press kit, profile highlights, portfolio, trust badge, and booking link on KalaManch.art.`}
        keywords={[hero.artist_name, hero.art_form, hero.city, 'artist EPK', 'press kit', 'KalaManch'].filter(Boolean).join(', ')}
      />
      <section className="mx-auto max-w-6xl px-5 py-10 lg:py-14">
        {status === 'loading' && <div className="rounded-3xl bg-white p-8 text-center font-black text-gray-600 shadow-xl">Building artist EPK...</div>}
        {status === 'error' && (
          <div className="mt-4 rounded-3xl bg-white p-8 text-center shadow-xl">
            <h1 className="text-3xl font-black text-gray-950">EPK not available yet.</h1>
            <Link className="btn-primary mt-5 inline-flex" to="/artists">
              Browse artists
            </Link>
          </div>
        )}
        {status === 'ready' && (
          <div className={isDark ? 'text-white' : 'text-gray-950'}>
            <div className="grid gap-8 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
              <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-[2rem] bg-white/80 p-3 shadow-2xl ring-1 ring-white/70">
                {hero.image_url ? (
                  <img
                    src={hero.image_url}
                    alt={hero.artist_name}
                    className="mx-auto aspect-[4/5] max-h-[520px] w-full rounded-[1.5rem] bg-gray-100 object-contain"
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <div className="flex aspect-[4/5] items-center justify-center rounded-[1.5rem] bg-white/20 text-6xl font-black">K</div>
                )}
              </div>
              <div>
                <p className={`text-xs font-black uppercase tracking-[0.35em] ${isDark ? 'text-orange-200' : 'text-[#FF6F00]'}`}>Living EPK</p>
                <h1 className="mt-4 text-5xl font-black leading-tight lg:text-7xl">{hero.artist_name}</h1>
                <p className={`mt-4 text-2xl font-black ${isDark ? 'text-orange-100' : 'text-[#4A148C]'}`}>{hero.tagline}</p>
                {blueprint.trust_badge?.is_verified && (
                  <span className="mt-6 inline-flex rounded-full bg-emerald-50 px-5 py-2 text-sm font-black text-emerald-700">
                    {blueprint.trust_badge.label}
                  </span>
                )}
                <p className={`mt-6 max-w-2xl text-lg leading-8 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{about.body}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={`/artist/${blueprint.slug}`} className="btn-primary">
                    View public profile
                  </Link>
                  <a href={cta.url || `/artist/${blueprint.slug}`} className="btn-outline bg-white">
                    Contact through KalaManch
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {(stats.items || []).map((item) => (
                <div key={item.label} className="rounded-3xl bg-white/90 p-6 text-gray-950 shadow-xl">
                  <p className="text-3xl font-black">{item.value}</p>
                  <p className="mt-1 text-sm font-bold text-gray-500">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_0.85fr]">
              <section className="rounded-[2rem] bg-white/95 p-6 text-gray-950 shadow-xl">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-[#FF6F00]">Portfolio</p>
                <h2 className="mt-3 text-3xl font-black">{highlights.title}</h2>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {(highlights.image_urls || []).slice(0, 6).map((url) => (
                    <img key={url} src={url} alt="" className="aspect-square rounded-2xl bg-gray-100 object-contain" loading="lazy" decoding="async" />
                  ))}
                </div>
                {(highlights.video_urls || []).length > 0 && (
                  <div className="mt-6 space-y-2">
                    {highlights.video_urls.slice(0, 4).map((url, index) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="block rounded-2xl border border-gray-100 px-4 py-3 font-bold text-[#4A148C]">
                        Performance video {index + 1}
                      </a>
                    ))}
                  </div>
                )}
              </section>
              <section className="rounded-[2rem] bg-white/95 p-6 text-gray-950 shadow-xl">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-[#FF6F00]">Services</p>
                <h2 className="mt-3 text-3xl font-black">{services.title}</h2>
                <div className="mt-5 flex flex-wrap gap-2">
                  {(services.items || []).map((item) => (
                    <span key={item} className="rounded-full bg-orange-50 px-4 py-2 text-sm font-black text-[#FF6F00]">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="mt-8 rounded-3xl bg-emerald-50 p-5">
                  <h3 className="text-xl font-black text-emerald-900">{cta.title}</h3>
                  <Link to={`/artist/${blueprint.slug}`} className="mt-4 inline-flex rounded-full bg-emerald-600 px-5 py-3 font-black text-white">
                    {cta.button_label}
                  </Link>
                </div>
              </section>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default ArtistEpk;
